import { HotmathPage } from '@nrdy-marketing-engine/hotmath/@types/hotmath-page';

interface Query {
  model: string;
  slug?: string;
  locale?: string;
  process?: { fn: string; [key: string]: any }[];
  authorImgixParams?: any;
  mediaImgixParams?: any;
  allPages?: boolean;
  skip?: number;
  limit?: number;
}

export interface Page {
  hero?: any;
  body: any;
  seoMetadata: {
    title: string;
    description: string;
    image?: any;
  };
  slug: string;
  indexable: boolean;
  leadForm?: Record<string, any>;
}

interface CatalogDetailPage extends Page {
  title: string;
}

export interface MexBlogArticle {
  slug: string;
  title: string;
  content: any;
  postedAt: string;
  author: {
    avatar: {
      responsiveImage: any;
    };
    name: string;
  };
}

type CacheStatus = 'hit' | 'miss' | 'skip' | 'miss-some';

export interface CmsResult {
  catalogDetailPage?: CatalogDetailPage;
  nmePage?: Page;
  page?: Page;
  ppcPage?: any;
  hotmathPage?: HotmathPage;
  leadForm?: Record<string, any>;
  allRoutes?: Record<string, any>;
  allMexBlogArticles?: MexBlogArticle[];
  allHotmathPages?: HotmathPage[];
  mexBlogArticle?: MexBlogArticle;
  cache?: CacheStatus;
}

function cacheStatus(results: { cache: string }[]): CacheStatus {
  const allCaches = [...new Set(results.map((r) => r.cache?.toLocaleLowerCase() as CacheStatus))];
  return allCaches.length > 1 ? 'miss-some' : allCaches[0];
}

/**
 * Request DatoCMS content from our internal proxy API, using one or more queries.
 *
 * @param queries Array of queries, each having `{ model, slug, locale, ...}`
 */
export default async function fetchNspData(...queries: Query[]): Promise<CmsResult> {
  const body = {
    environment: process.env.NEXT_PUBLIC_DATOCMS_ENVIRONMENT || undefined,
    preview: process.env.NEXT_PUBLIC_DATOCMS_PREVIEW == '1' ? true : undefined,
    queries,
  };

  if (process.env.DEBUG == '1') console.debug('fetchNspData', body);

  const res = await fetch(process.env.NEXT_PUBLIC_DATOCMS_PROXY_ENDPOINT, {
    method: 'post',
    body: JSON.stringify(body),
  });

  const data = await res.json();
  if (data.error) {
    throw new Error(`API Error: ${data.error}`);
  }

  if (process.env.DEBUG == '1') console.debug('nspData', data);

  // Take all the results and merge them together into a single object
  return {
    cache: cacheStatus(data.results),
    ...data.results.reduce((memo, result) => ({ ...memo, ...result.data }), {}),
  };
}
